import React from 'react';
import { Button, Card, Form, Input, Select, Space, Tabs, Tooltip } from 'antd';

import { ApiError, SuperGroup } from '@services/types';

import styles from './create-user-modal.module.scss';
import { getUserRoleByRoles, selectSearchFilter, validatePassword } from '@services/utils';
import { Role, UserRequest } from '@openapi';
import { toast } from 'react-toastify';
import api from '@services/api';
import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import { getPasswordRequirementsTooltip } from '@services/passwordRecommendation';
import { AppContext } from '@layouts/AppRouter';
import MfaSwitch from 'component/MfaSwitch';
import MfaReset from 'component/MfaReset';

const { TabPane } = Tabs;

const CreateUserModal = () => {
  const { variablesProfile } = React.useContext(AppContext);
  const isSaml = variablesProfile?.profile === 'saml';
  const [form] = Form.useForm<Omit<UserRequest, 'id'>>();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = React.useState<any>();
  const [role, setRole] = React.useState<UserRequest['role'] | any>();
  const [superGroups, setSuperGroups] = React.useState<SuperGroup[]>([]);
  const [activeTab, setActiveTab] = React.useState('info');
  const [emailHasUppercase, setEmailHasUppercase] = React.useState(false);

  const onClose = () => {
    history.push(Navigation.Administration);
  };
  React.useEffect(() => {
    api.administration.getSuperGroups().then(setSuperGroups);
  }, []);
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('🚀 ~ file: CreateUserModal.tsx ~ line 31 ~ handleSubmit ~ values', values);
      if (user) {
        await api.administration.updateUser(user.id, values);
        toast.success('User was updated successfully');
      } else {
        await api.administration.createUser(values);
        toast.success('User was created successfully');
      }
      form.resetFields();
      setEmailHasUppercase(false);
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(`${error.error.details}`);
      } else {
        toast.error('User was not updated/created due to error');
      }
      console.log('CreateUserModal ~ handleSubmit ~ error', error);
    }
  };

  React.useEffect(() => {
    if (id) {
      api.administration.getUserById(id).then((data) => {
        if (data) {
          setUser(data);
          setRole(getUserRoleByRoles(data?.roles || []));
        }
      });
    }
  }, []);
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };
  return user ? (
    <Card
      title={
        <span>
          {user.firstName} {user.lastName}
        </span>
      }
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
            Back to Users Page
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Update
          </Button>
        </Space>
      }
    >
      <Tabs onChange={handleTabChange} type="card" size="large" defaultActiveKey={'info'}>
        <TabPane tab={user ? 'Info' : 'Add Info'} key="info">
          <Form
            form={form}
            layout="vertical"
            name="form_in_modal"
            className={styles.form}
            initialValues={{
              email: '',
              firstName: '',
              lastName: '',
              password: '',
              ...(user || {}),
              superGroupIds: user?.etc?.perimeter || [],
              role: getUserRoleByRoles(user?.roles || []),
            }}
          >
            <Form.Item
              style={{ gridArea: 'email' }}
              name="email"
              label="Email"
              rules={[
                {
                  required: true,
                  type: 'email',
                  message: 'Please input the email of user!',
                },
              ]}
              help={emailHasUppercase ? 'Email will be automatically converted to lowercase before saving' : undefined}
            >
              <Input
                type="email"
                autoComplete={'false'}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmailHasUppercase(/[A-Z]/.test(value));
                }}
              />
            </Form.Item>
            <Form.Item
              name="firstName"
              style={{ gridArea: 'firstName' }}
              label="First Name"
              rules={[
                {
                  required: true,
                  message: 'Please input the firstName of user!',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              style={{ gridArea: 'lastName' }}
              label="Last Name"
              rules={[
                {
                  required: true,
                  message: 'Please input the lastName of user!',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="role"
              style={{ gridArea: 'role' }}
              label="Role"
              rules={[
                {
                  required: true,
                  message: 'Please input the role of user!',
                },
              ]}
            >
              <Select
                allowClear
                onChange={(value: Role) => {
                  setRole(value);
                }}
                showSearch
                filterOption={selectSearchFilter}
              >
                <Select.Option value="SUPERADMIN">Super Admin</Select.Option>
                <Select.Option value="INTEGRATION">Integration</Select.Option>
                <Select.Option value="QUALITY">Quality</Select.Option>
                <Select.Option value="PARTNER">Partner</Select.Option>
              </Select>
            </Form.Item>
            {[Role.Quality, Role.Integration].includes(role) && (
              <Form.Item name="superGroupIds" style={{ gridArea: 'supergroups' }} label="Scope">
                <Select mode="multiple" allowClear filterOption={selectSearchFilter}>
                  {superGroups.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form>
        </TabPane>
        {user && (
          <TabPane tab="Security" key="security">
            {activeTab === 'security' && (
              <Form
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  columnGap: '35px',
                }}
                form={form}
                layout="vertical"
                name="form_in_modal"
                initialValues={{
                  password: '',
                  mfaEnabled: user.mfaEnabled,
                  shouldResetMfa: false,
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr max-content',
                    gridGap: '10px',
                    alignItems: 'center',
                  }}
                >
                  <Form.Item
                    name="password"
                    label="New Password"
                    rules={[
                      () => ({
                        validator(_, value) {
                          const error = validatePassword(value, variablesProfile?.minPasswordSize);
                          if (error) {
                            return Promise.reject(new Error(error));
                          } else {
                            return Promise.resolve();
                          }
                        },
                      }),
                    ]}
                    hasFeedback
                  >
                    <Input.Password />
                  </Form.Item>
                  <Tooltip placement="right" title={getPasswordRequirementsTooltip(variablesProfile?.minPasswordSize)}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>

                <Form.Item
                  name="confirm"
                  label="Confirm Password"
                  dependencies={['password']}
                  hasFeedback
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }

                        return Promise.reject(new Error('The two passwords that you entered do not match!'));
                      },
                    }),
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                {!isSaml && (
                  <Form.Item name="mfaEnabled" valuePropName="checked">
                    <MfaSwitch />
                  </Form.Item>
                )}
                {!isSaml && (
                  <Form.Item noStyle shouldUpdate={(prev, curr) => prev.mfaEnabled !== curr.mfaEnabled}>
                    {({ getFieldValue }) =>
                      getFieldValue('mfaEnabled') &&
                      user.mfaEnabled && (
                        <Form.Item name="shouldResetMfa" valuePropName="checked">
                          <MfaReset />
                        </Form.Item>
                      )
                    }
                  </Form.Item>
                )}
              </Form>
            )}
          </TabPane>
        )}
      </Tabs>
    </Card>
  ) : !id ? (
    <Card
      title="Create a new user"
      extra={
        <Space>
          <Button icon={<ArrowLeftOutlined />} type="default" onClick={onClose}>
            Back to Users Page
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Create
          </Button>
        </Space>
      }
    >
      <Tabs type="card" size="large" defaultActiveKey={'info'}>
        <TabPane tab="Add Info" key="info">
          <Form
            form={form}
            layout="vertical"
            name="form_in_modal"
            className={styles.form}
            initialValues={{
              email: '',
              firstName: '',
              lastName: '',
              password: '',
              ...(user || {}),
              superGroupIds: user?.etc?.perimeter || [],
              role: getUserRoleByRoles(user?.roles || []),
            }}
          >
            <Form.Item
              style={{ gridArea: 'email' }}
              name="email"
              label="Email"
              rules={[
                {
                  required: true,
                  type: 'email',
                  message: 'Please input the email of user!',
                },
              ]}
              help={emailHasUppercase ? 'Email will be automatically converted to lowercase before saving' : undefined}
            >
              <Input
                type="email"
                autoComplete={'false'}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmailHasUppercase(/[A-Z]/.test(value));
                }}
              />
            </Form.Item>
            <Form.Item
              name="firstName"
              style={{ gridArea: 'firstName' }}
              label="First Name"
              rules={[
                {
                  required: true,
                  message: 'Please input the firstName of user!',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="lastName"
              style={{ gridArea: 'lastName' }}
              label="Last Name"
              rules={[
                {
                  required: true,
                  message: 'Please input the lastName of user!',
                },
              ]}
            >
              <Input />
            </Form.Item>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridGap: '35px',
                gridArea: 'password',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr max-content',
                  gridGap: '10px',
                  alignItems: 'center',
                }}
              >
                <Form.Item
                  name="password"
                  label="New Password"
                  rules={[
                    {
                      required: true,
                      message: 'Please input your password!',
                    },
                    () => ({
                      validator(_, value) {
                        const error = validatePassword(value, variablesProfile?.minPasswordSize);
                        if (error) {
                          return Promise.reject(new Error(error));
                        } else {
                          return Promise.resolve();
                        }
                      },
                    }),
                  ]}
                  hasFeedback
                >
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Tooltip placement="right" title={getPasswordRequirementsTooltip(variablesProfile?.minPasswordSize)}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>

              <Form.Item
                name="confirm"
                label="Confirm Password"
                dependencies={['password']}
                hasFeedback
                rules={[
                  {
                    required: true,
                    message: 'Please confirm your password!',
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error('The two passwords that you entered do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>
            </div>
            <Form.Item
              name="role"
              style={{ gridArea: 'role' }}
              label="Role"
              rules={[
                {
                  required: true,
                  message: 'Please input the role of user!',
                },
              ]}
            >
              <Select
                allowClear
                onChange={(value: Role) => {
                  setRole(value);
                }}
                showSearch
                filterOption={selectSearchFilter}
              >
                <Select.Option value="SUPERADMIN">Super Admin</Select.Option>
                <Select.Option value="INTEGRATION">Integration</Select.Option>
                <Select.Option value="QUALITY">Quality</Select.Option>
                <Select.Option value="PARTNER">Partner</Select.Option>
              </Select>
            </Form.Item>
            {[Role.Quality, Role.Integration].includes(role) && (
              <Form.Item name="superGroupIds" style={{ gridArea: 'supergroups' }} label="Scope">
                <Select mode="multiple" allowClear filterOption={selectSearchFilter}>
                  {superGroups.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Form>
        </TabPane>
      </Tabs>
    </Card>
  ) : null;
};

export default React.memo(CreateUserModal);
